import { Component, HostListener, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterOutlet } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FooterComponent } from '../components/footer/footer.component';
import { HeaderComponent } from '../components/header/header.component';
import { PatientSummaryComponent } from '../components/patient-summary/patient-summary.component';
import { ProgressFlowComponent } from '../components/progress-flow/progress-flow.component';
import { ProgressTrackerComponent } from '../components/progress-tracker/progress-tracker.component';
import { FooterContent } from '../models/content/footer-content.model';
import { GetEntryRequest } from '../models/content/get-entry-request.model';
import { HeaderContent } from '../models/content/header-content.model';
import { ContentType } from '../models/enums/content-type';
import { BreakpointService } from '../services/breakpoint.service';
import { ContentService } from '../services/content.service';
import { PendoService } from '../services/pendo.service';
import { SpinnerService } from '../services/spinner.service';
import { TimerService } from '../services/timer.service';
import { WorkflowService } from '../services/workflow.service';
import { DataService } from '../services/data.service';

import { NgxSkeletonLoaderComponent } from "ngx-skeleton-loader";

@Component({
    selector: 'seth-root',
    imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    PatientSummaryComponent,
    ProgressTrackerComponent,
    ProgressFlowComponent,
    MatProgressSpinnerModule,
    NgxSkeletonLoaderComponent,
],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private _contentService = inject(ContentService);
  private _destroy$: Subject<boolean> = new Subject<boolean>();
  private _breakpointService = inject(BreakpointService);
  private _spinnerService = inject(SpinnerService);
  private _router: Router = inject(Router);
  private _workflowService = inject(WorkflowService);
  private _timerService: TimerService = inject(TimerService);
  private _pendoService: PendoService = inject(PendoService);
  private _dataService: DataService = inject(DataService);
  isMobile$: WritableSignal<boolean> = this._breakpointService.isMobileSize;
  isTabletSize$: WritableSignal<boolean> = this._breakpointService.isTabletSize;
  isDesktop$: WritableSignal<boolean> = this._breakpointService.isDesktopSize;
  isLoading$: WritableSignal<boolean> = this._spinnerService.loading$;

  footerContent!: FooterContent;
  headerContent!: HeaderContent;
  footerExists: WritableSignal<boolean> = signal<boolean>(false);
  headerExists: WritableSignal<boolean> = signal<boolean>(false);
  maxTimeInMinutes = 15;
  timerSubscription: Subscription | undefined;

  ngOnInit(): void {
    this.headerExists.set(false);
    this.footerExists.set(false);
    const request = this._dataService.getEntryRequest$();
    const footerRequest: GetEntryRequest = {
      ...request,
      contentType: ContentType.footer,
    };
    const headerRequest: GetEntryRequest = {
      ...request,
      contentType: ContentType.header,
    };
    this._contentService
      .getEntry(footerRequest)
      .pipe(takeUntil(this._destroy$))
      .subscribe((content) => {
        this.footerExists.set(true);
        this.footerContent = content;
      });

    this._contentService
      .getEntry(headerRequest)
      .pipe(takeUntil(this._destroy$))
      .subscribe((content) => {
        this.headerExists.set(true);
        this.headerContent = content;
      });

    this.isLoading$.set(false);

    this.timerSubscription = this._timerService.startTimer(this.maxTimeInMinutes * 60000).subscribe({
      complete: () => {
        this._workflowService.previousRoute$.set(this._workflowService.currentRoute);
        this._router.navigate(['']);
      },
    });

    this.resetTimer();
    this._pendoService.install();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }

  @HostListener('document:click')
  @HostListener('document:keydown')
  resetTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = this._timerService.startTimer(this.maxTimeInMinutes * 60000).subscribe({
        complete: () => {
          this._workflowService.previousRoute$.set(this._workflowService.currentRoute);
          this._router.navigate(['']);
        },
      });
    }
  }

}
